import { FrontendFilterValue } from '../types/filters.type';

// These regular expressions match on common CSV delimiters, such as commas, newlines and pipes.
// There are two as in some places, we want to split on spaces, but not in others
const delimitersExcludingSpaces: RegExp = /,\s*|\n+|\t+|\|+|\u00A0+|\u200B+|\uFEFF+/;
const delimitersIncludingSpaces: RegExp = /,\s*|[\n|\u00A0\u200B\uFEFF]+|\s+/;

export enum DelimiterRules {
  SpacesAreDelimiter = 'spaces_are_delimiter',
  SpacesAreNotDelimiter = 'spaces_are_not_delimiter'
}

export class PasteValueHandler {
  private readonly delimiterRegex: RegExp;

  constructor(delimiter: DelimiterRules) {
    if (delimiter === DelimiterRules.SpacesAreDelimiter) {
      this.delimiterRegex = delimitersIncludingSpaces;
    } else {
      this.delimiterRegex = delimitersExcludingSpaces;
    }
  }

  createPastedValues(input: string): string[] {
    const cleanedInput = input.split(this.delimiterRegex).map((value) => value.trim()).filter((value) => value.length > 0);
    return Array.from(new Set(cleanedInput));
  }

  /**
   * Given a comma-separated string, plus a list of valid items,
   * produce two groups of matched and unmatched items.
   *
   * Returned matched items are ordered by their id, while unmatched items
   * are ordered alphabetically
   */
  parseAndMatchFilterValue(
    input: string,
    listToCheck: readonly FrontendFilterValue[],
  ): {
    matched: FrontendFilterValue[],
    unMatched: string[]
  } {
    // Convert the list of valid items into a dictionary - this makes it easier to
    // figure out if an item is valid or not, via a dictionary lookup
    //
    // Case sensitity is checked via uppercasing everything
    const dictToCheck: { [id: string]: FrontendFilterValue } = Object.fromEntries(
      listToCheck.map((item) => [item.displayName.toUpperCase(), item])
    );
    const matchedSet: Set<FrontendFilterValue> = new Set();
    const unmatchedSet: Set<string> = new Set();

    // Split the input string into tokens, trim them of whitespace, and drop empty items
    //
    // Items are then checked against our valid dict, and then bucketed into either
    // the matched set or the unmatched set. As they are sets, duplicates will be discarded.
    input.split(this.delimiterRegex).forEach((item) => {
      const trimmed = item.trim();
      if (!trimmed) {
        return;
      }
      const uppercased = trimmed.toUpperCase();
      const match = dictToCheck[uppercased];

      if (match) {
        matchedSet.add(match);
      } else {
        unmatchedSet.add(trimmed);
      }
    });

    const matched = Array.from(matchedSet);
    const unMatched = Array.from(unmatchedSet);
    matched.sort((a, b) => a.id.localeCompare(b.id));
    unMatched.sort((a, b) => a.localeCompare(b));
    return { matched, unMatched };
  }

  isPastedValue(value: string): boolean {
    return this.delimiterRegex.test(value);
  }
}
